import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import moment from "moment"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync } from "@fortawesome/free-solid-svg-icons"
import {
	fetchDepannageToDispatch,
	fetchDispatchEnAttente,
} from "../../../store/actions/depannageDispatchActions"
import {
	DEP_STATE_CHECK_DATA,
	DEP_STATE_EN_ATTENTE,
	DEP_STATE_READY_TO_DISPATCH,
	DEPANNAGE_TYPE_APPEL_ASSISTANCE,
	DEPANNAGE_TYPE_APPEL_CHOICES,
} from "../../../utilities/Addons"
import { COLORS } from "../../../utilities/Constants"
import {
	buildSecteurParCode,
	getSecteurParCode,
} from "../../../utilities/depSecteurs"
import { generalStyles as gs } from "../../styles/styles"
import Loader from "../../basics/Loader"
import DepMissionV2 from "../depmission/DepMissionV2"
import DepDispatchDispatch from "./DepDispatchDispatch"
import { useMissionsModifiees } from "../../../utilities/notificationsSocket"
import DepMissionCardV2 from "./DepMissionCardV2"
import "./DepDispatch.css"

// Cadence du rafraîchissement automatique de l'onglet (le poste de dispatch
// est multi-opérateurs : les cartes se périmaient en silence avant).
export const DISPATCH_POLL_MS = 45000

// Filtre d'état : sentinelle « Tous » + les 3 états que l'onglet expose.
const ETAT_TOUS = -1
const ETAT_OPTIONS = [
	{ value: ETAT_TOUS, label: "Tous" },
	{ value: DEP_STATE_CHECK_DATA, label: "À vérifier" },
	{ value: DEP_STATE_READY_TO_DISPATCH, label: "Prêt" },
	{ value: DEP_STATE_EN_ATTENTE, label: "En attente" },
]
const APPEL_TOUS = -1
// Filtre assistance : "" = « Toutes ». On filtre par LIBELLÉ d'assistance
// (assistance_entite_label) — seul champ garanti présent sur le payload du
// dispatch (l'id assistance_entite n'est pas toujours sérialisé).
const ASSIST_TOUTES = ""
// Filtre secteur : sentinelles distinctes de tout `nom` de secteur réel.
const SECTEUR_TOUS = "__tous__"
const SECTEUR_INCONNU = "__inconnu__"

// Onglet « À traiter » : missions en vérification (10) / prêtes au dispatch (20)
// / en attente (25), fusionnées en une seule grille filtrable (état, type
// d'appel, secteur géographique). Le secteur est dérivé du code postal du lieu
// d'intervention via le mapping SecteurGeographique → codes postaux (config).
const DepDispatchATraiter = () => {
	const dispatch = useDispatch()
	const [lastUpdate, setLastUpdate] = useState(moment())
	const [openDmModal, setOpenDmModal] = useState(false)
	const [openDmModalDispatch, setOpenDmModalDispatch] = useState(false)
	const [depMission, setDepMission] = useState(null)

	// État des filtres — conservé à travers les rafraîchissements automatiques
	// (state local, jamais réinitialisé par un poll).
	const [etatFilter, setEtatFilter] = useState(ETAT_TOUS)
	const [appelFilter, setAppelFilter] = useState(APPEL_TOUS)
	const [assistanceFilter, setAssistanceFilter] = useState(ASSIST_TOUTES)
	const [secteurFilter, setSecteurFilter] = useState(SECTEUR_TOUS)

	const {
		depsToDispatch,
		fetchDepsToDispatch,
		depsEnAttente,
		fetchDepsEnAttente,
		putDepsToDispatch,
	} = useSelector((state) => state.depannageDispatchInfo)
	const secteurs = useSelector((state) => state.autreInfo.secteurs)
	// Référentiel des assistances (Entite type=ASSISTANCE), chargé par
	// fetchAutreEntites() au montage du menu dispatch.
	const assistances = useSelector((state) => state.autreInfo.assistances)

	const fetchDepMissions = () => {
		dispatch(
			fetchDepannageToDispatch({
				etat: [DEP_STATE_CHECK_DATA, DEP_STATE_READY_TO_DISPATCH],
			}),
		).then(() => setLastUpdate(moment()))
		dispatch(fetchDispatchEnAttente())
	}

	useEffect(() => {
		fetchDepMissions()
		const interval = setInterval(fetchDepMissions, DISPATCH_POLL_MS)
		return () => clearInterval(interval)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (putDepsToDispatch.saved) fetchDepMissions()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [putDepsToDispatch.saved])
	// GE-62 (revue phase B, 🟠5) : temps réel + relecture après un 409
	// (rouvrir la mission repart alors de la version actuelle).
	// Revue 5 (🟡) : seulement les missions de l'onglet (avant dispatch, 10
	// à 27) ou affichées (qui en sortent).
	useMissionsModifiees(fetchDepMissions, {
		filtre: (d) =>
			(d.etat >= 10 && d.etat < 30) ||
			[...depsToDispatch, ...depsEnAttente].some((m) => m.id === d.id),
	})
	useEffect(() => {
		const e = putDepsToDispatch.error
		if (e && e.httpStatus === 409) fetchDepMissions()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [putDepsToDispatch.error])

	// Table code postal → nom de secteur, reconstruite quand la config change.
	const secteurParCode = useMemo(
		() => buildSecteurParCode(secteurs),
		[secteurs],
	)

	// Liste des secteurs (triée) pour le select ; « Inconnu » n'apparaît que si
	// au moins une mission n'a pas de secteur dérivable.
	const secteursTries = useMemo(
		() =>
			[...(secteurs || [])].sort((a, b) =>
				(a.nom || "").localeCompare(b.nom || "", "fr"),
			),
		[secteurs],
	)

	const getSecteurNom = (dm) => {
		const cp =
			dm &&
			dm.lieu_intervention &&
			dm.lieu_intervention.adresse &&
			dm.lieu_intervention.adresse.code_postal
		return getSecteurParCode(secteurParCode, cp)
	}

	// Missions fusionnées (à traiter 10/20 + en attente 25), dédupliquées par id.
	// Tri primaire par état DESCendant (en attente 25 > prêt 20 > vérification
	const missions = useMemo(() => {
		const aTraiter = (depsToDispatch || []).filter((dm) =>
			[DEP_STATE_CHECK_DATA, DEP_STATE_READY_TO_DISPATCH].includes(
				dm.etat,
			),
		)
		const seen = new Set()
		const out = []
		;[...aTraiter, ...(depsEnAttente || [])].forEach((dm) => {
			if (dm && !seen.has(dm.id)) {
				seen.add(dm.id)
				out.push(dm)
			}
		})
		return out.sort(
			(a, b) =>
				b.etat - a.etat || new Date(b.cree_le) - new Date(a.cree_le),
		)
	}, [depsToDispatch, depsEnAttente])

	// Options du filtre assistance = tout le référentiel des assistances (triées),
	// pas seulement celles en file. On filtre les missions par LIBELLÉ (le label
	// du référentiel == mission.assistance_entite_label, tous deux Entite.label).
	const assistanceOptions = useMemo(
		() =>
			[...(assistances || [])]
				.filter((a) => a && a.label)
				.sort((a, b) => a.label.localeCompare(b.label, "fr")),
		[assistances],
	)

	// Application des filtres appel (+ assistance) et secteur ; l'état est filtré
	// à part pour pouvoir afficher les compteurs par état dans le segmented.
	const matchAppel = (dm) => {
		if (appelFilter !== APPEL_TOUS && dm.appel_type !== appelFilter)
			return false
		// Sous-filtre assistance : uniquement quand on est sur « Assistance ».
		if (
			appelFilter === DEPANNAGE_TYPE_APPEL_ASSISTANCE &&
			assistanceFilter !== ASSIST_TOUTES &&
			dm.assistance_entite_label !== assistanceFilter
		)
			return false
		return true
	}
	const matchSecteur = (dm) => {
		if (secteurFilter === SECTEUR_TOUS) return true
		const nom = getSecteurNom(dm)
		if (secteurFilter === SECTEUR_INCONNU) return !nom
		return nom === secteurFilter
	}

	// Filtre appel d'abord ; le secteur s'applique par-dessus.
	const appelFiltre = missions.filter(matchAppel)
	const baseFiltre = appelFiltre.filter(matchSecteur)
	// « Inconnu » n'apparaît que sur « Tous secteurs » (ou s'il est déjà
	// sélectionné) : dès qu'un secteur précis est choisi, l'option disparaît.
	const showInconnu =
		appelFiltre.some((dm) => !getSecteurNom(dm)) &&
		(secteurFilter === SECTEUR_TOUS || secteurFilter === SECTEUR_INCONNU)

	// Changer de type d'appel remet le sous-filtre assistance à zéro (évite un
	// filtrage résiduel invisible quand le select d'assistance est masqué).
	const changeAppel = (val) => {
		setAppelFilter(val)
		setAssistanceFilter(ASSIST_TOUTES)
	}

	const etatCounts = useMemo(() => {
		const c = { [ETAT_TOUS]: baseFiltre.length }
		ETAT_OPTIONS.forEach((o) => {
			if (o.value !== ETAT_TOUS)
				c[o.value] = baseFiltre.filter(
					(dm) => dm.etat === o.value,
				).length
		})
		return c
	}, [baseFiltre])

	const visibles = baseFiltre.filter(
		(dm) => etatFilter === ETAT_TOUS || dm.etat === etatFilter,
	)

	const openVerification = (dm) => {
		setDepMission(dm)
		setOpenDmModal(true)
	}
	const openTraiter = (dm) => {
		setDepMission(dm)
		setOpenDmModalDispatch(true)
	}

	const loading = fetchDepsToDispatch.loading || fetchDepsEnAttente.loading
	const isFetching = fetchDepsToDispatch.loading

	return (
		<div className="dep-dispatch-v2">
			{openDmModal && (
				<DepMissionV2
					closeModal={() => {
						setOpenDmModal(false)
						setDepMission(null)
					}}
					refreshDeps={fetchDepMissions}
					visible={openDmModal}
					editDepMission={depMission}
				/>
			)}
			{openDmModalDispatch && (
				<DepDispatchDispatch
					closeModal={() => {
						setOpenDmModalDispatch(false)
						setDepMission(null)
					}}
					visible={openDmModalDispatch}
					dm={depMission}
				/>
			)}

			<div
				style={st.toolbar}
				role="region"
				aria-label="Filtres du dispatch"
			>
				<div style={st.filters}>
					<div style={st.filterGroup}>
						<span style={st.filterLabel} id="filtre-etat-label">
							État
						</span>
						<div
							style={gs.segmented}
							role="group"
							aria-labelledby="filtre-etat-label"
						>
							{ETAT_OPTIONS.map((o) => {
								const active = etatFilter === o.value
								return (
									<button
										key={`etat_${o.value}`}
										type="button"
										style={gs.segment(active)}
										aria-pressed={active}
										onClick={() => setEtatFilter(o.value)}
									>
										{o.label}
										<span style={st.segCount}>
											{etatCounts[o.value] ?? 0}
										</span>
									</button>
								)
							})}
						</div>
					</div>

					<div style={st.filterGroup}>
						<span style={st.filterLabel} id="filtre-appel-label">
							Type d'appel
						</span>
						<div
							style={st.chipRow}
							role="group"
							aria-labelledby="filtre-appel-label"
						>
							{[
								[APPEL_TOUS, "Tous"],
								...DEPANNAGE_TYPE_APPEL_CHOICES,
							].map(([val, lbl]) => {
								const active = appelFilter === val
								return (
									<button
										key={`appel_${val}`}
										type="button"
										style={chip(active)}
										aria-pressed={active}
										onClick={() => changeAppel(val)}
									>
										{lbl}
									</button>
								)
							})}
						</div>
					</div>

					{appelFilter === DEPANNAGE_TYPE_APPEL_ASSISTANCE && (
						<div style={st.filterGroup}>
							<span
								style={st.filterLabel}
								id="filtre-assist-label"
							>
								Assistance
							</span>
							<select
								style={{
									...gs.select,
									minWidth: 160,
									flex: "none",
								}}
								aria-labelledby="filtre-assist-label"
								value={assistanceFilter}
								onChange={(e) =>
									setAssistanceFilter(e.target.value)
								}
							>
								<option value={ASSIST_TOUTES}>Toutes</option>
								{assistanceOptions.map((a) => (
									<option
										key={`assist_${a.id}`}
										value={a.label}
									>
										{a.label}
									</option>
								))}
							</select>
						</div>
					)}

					<div style={st.filterGroup}>
						<span style={st.filterLabel} id="filtre-secteur-label">
							Secteur géographique
						</span>
						<select
							style={{
								...gs.select,
								minWidth: 160,
								flex: "none",
							}}
							aria-labelledby="filtre-secteur-label"
							value={secteurFilter}
							onChange={(e) => setSecteurFilter(e.target.value)}
						>
							<option value={SECTEUR_TOUS}>Tous</option>
							{secteursTries.map((s) => (
								<option key={`sect_${s.id}`} value={s.nom}>
									{s.nom || "—"}
								</option>
							))}
							{showInconnu && (
								<option value={SECTEUR_INCONNU}>Inconnu</option>
							)}
						</select>
					</div>
				</div>

				<div style={st.refresh}>
					<span style={st.maj} aria-live="polite">
						{`Mis à jour à ${lastUpdate.format("HH:mm:ss")} — auto toutes les ${
							DISPATCH_POLL_MS / 1000
						} s`}
					</span>
					<button
						type="button"
						style={gs.iconBtn(COLORS.text)}
						onClick={fetchDepMissions}
						title="Rafraîchir maintenant"
						aria-label="Rafraîchir maintenant"
					>
						<FontAwesomeIcon icon={faSync} spin={isFetching} />
					</button>
				</div>
			</div>

			<h2 style={st.sectionTitle}>
				Missions à dispatcher
				<span style={st.count} aria-live="polite">
					{visibles.length}
				</span>
			</h2>

			{loading && missions.length === 0 ? (
				<Loader variant="ring" message="Chargement…" />
			) : visibles.length === 0 ? (
				<div style={st.empty}>
					Aucune mission ne correspond aux filtres
				</div>
			) : (
				<div
					style={st.grid}
					role="list"
					aria-label="Missions à dispatcher"
				>
					{visibles.map((dm) => (
						<DepMissionCardV2
							key={`dm_${dm.id}`}
							dep={dm}
							onVerifier={openVerification}
							onTraiter={openTraiter}
							onEdit={openVerification}
						/>
					))}
				</div>
			)}
		</div>
	)
}

// Chip pill toggle (aligné sur DepFicheInterventionV2) — filtre par type d'appel.
const chip = (active) => ({
	padding: "6px 14px",
	borderRadius: 99,
	border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
	backgroundColor: active ? COLORS.accentLight : COLORS.surface,
	color: active ? COLORS.accent : COLORS.textMuted,
	fontSize: 12,
	fontWeight: 600,
	cursor: "pointer",
	fontFamily: "inherit",
})

const st = {
	toolbar: {
		position: "sticky",
		top: 0,
		zIndex: 5,
		display: "flex",
		alignItems: "flex-end",
		justifyContent: "space-between",
		flexWrap: "wrap",
		gap: 16,
		padding: "12px 0",
		marginBottom: 4,
		backgroundColor: COLORS.bg,
		borderBottom: `1px solid ${COLORS.border}`,
	},
	filters: {
		display: "flex",
		flexWrap: "wrap",
		alignItems: "flex-end",
		gap: 20,
	},
	filterGroup: {
		display: "flex",
		flexDirection: "column",
		gap: 6,
	},
	filterLabel: {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		color: COLORS.textLight,
	},
	chipRow: {
		display: "flex",
		flexWrap: "wrap",
		gap: 8,
		alignItems: "center",
		// Aligne la hauteur des chips sur le segmented (bordure 1px + padding 4).
		paddingTop: 1,
		paddingBottom: 1,
	},
	segCount: {
		marginLeft: 6,
		fontVariantNumeric: "tabular-nums",
		color: COLORS.textLight,
		fontWeight: 700,
	},
	refresh: {
		display: "flex",
		alignItems: "center",
		gap: 10,
	},
	maj: { fontSize: 12, color: COLORS.textLight },
	sectionTitle: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		color: COLORS.textLight,
		margin: "14px 0 10px",
	},
	count: {
		padding: "1px 8px",
		borderRadius: 99,
		fontSize: 11,
		fontWeight: 700,
		color: COLORS.accent,
		backgroundColor: COLORS.accentLight,
	},
	grid: {
		display: "flex",
		flexWrap: "wrap",
		gap: 12,
	},
	empty: {
		padding: "24px 0",
		fontSize: 13,
		color: COLORS.textLight,
	},
}

export default DepDispatchATraiter
